/* eslint-disable react-hooks/exhaustive-deps */
import {useEffect, useRef, useState} from "react";
import {io} from "socket.io-client";
import {useSelector, useDispatch} from "react-redux";
import {showSnackbar, showTemplateSnackbar} from "../actions/snackbar";
import {useMessageHandler} from "./useMessageHandler";
import {useContactHandler} from "./useContactHandler";
import {updateContact} from "../actions/assignedContacts";
import {updateAgentPresence} from "../actions/agentes";
import {playNotificationSound} from "../notifications/notificationsPlayer";
import {buildAuthPayload} from "../utils/fingerprint";
import {isMessageDuplicate} from "../utils/messageDedup";
import {
    getNotificationPreferences,
    saveNotificationPreferences,
    shouldShowNotification,
    resolveInboxTone,
} from "../notifications/notificationSettings";
import {getSoundIdByUrl} from "../notifications/notificationSounds";
import {showIncomingDesktopNotification} from "../notifications/desktopNotifications";
import {trayQueueSyncContact, setActiveTray} from "../reducers/trays";
import {selectIdAgent} from "../actions/agentes";

export const useSocketLegacy = (path) => {

    const [socket, setSocket] = useState(null);
    const [online, setOnline] = useState(false);

    const dispatch = useDispatch();

    const agent = useSelector((state) => state.agent);
    const {phone, id_customer, _id, email} = agent;

    const {contactsMessages, selectContactMessages} = useSelector(
        (state) => state.messages
    );
    const {contactsAssigneds} = useSelector(
        (state) => state.assignedContact
    );
    const {contactDetails, savedContactDetails} = useSelector(
        (state) => state.contact
    );

    const activeTrayId = useSelector((state) => state.trays?.activeTrayId);

    /* =========================
       🧠 Refs anti-closure
    ========================= */

    const refContactMessages = useRef(contactsMessages);
    const refAssignedAgentContacts = useRef(contactsAssigneds);
    const refSelectContactMessages = useRef(selectContactMessages);
    const refSavedContactDetails = useRef(savedContactDetails);
    const refContactDetails = useRef(contactDetails);
    const refIdMyAgent = useRef(_id);
    const refAgentNotificationSettings = useRef(agent?.notification_settings);
    const refActiveTrayId = useRef(activeTrayId);
    const joinedRef = useRef(false);

    const lastNotificationRef = useRef({
        key: null,
        at: 0,
    });

    /* =========================
       🧩 Handlers externos
    ========================= */

    const {handleMessageActions, isDuplicate} = useMessageHandler({
        refContactDetails,
        refSelectContactMessages,
        refContactMessages,
        refSavedContactDetails,
        refIdMyAgent,
    });

    const {handleAgentMove, updateContactReceived} = useContactHandler({
        refContactDetails,
        refAssignedAgentContacts,
        refIdMyAgent,
    });

    /* =========================
       🔄 Sync refs
    ========================= */

    useEffect(() => { refIdMyAgent.current = _id }, [_id]);
    useEffect(() => { refSelectContactMessages.current = selectContactMessages }, [selectContactMessages]);
    useEffect(() => { refContactDetails.current = contactDetails }, [contactDetails]);
    useEffect(() => { refContactMessages.current = contactsMessages }, [contactsMessages]);
    useEffect(() => { refAssignedAgentContacts.current = contactsAssigneds }, [contactsAssigneds]);
    useEffect(() => { refSavedContactDetails.current = savedContactDetails }, [savedContactDetails]);
    useEffect(() => { refAgentNotificationSettings.current = agent?.notification_settings }, [agent?.notification_settings]);
    useEffect(() => { refActiveTrayId.current = activeTrayId }, [activeTrayId]);

    /* =========================
       🔔 Notification settings
    ========================= */

    useEffect(() => {
        const personalInbox = agent?.notification_settings?.personal_inbox;
        if (!personalInbox) return;

        const current = getNotificationPreferences();
        const toneFromAgent = getSoundIdByUrl(personalInbox.tone);
        const enabledFromAgent =
            typeof personalInbox.enabled === "boolean"
                ? personalInbox.enabled
                : current.enabled;

        if (
            current.tone !== toneFromAgent ||
            current.enabled !== enabledFromAgent
        ) {
            saveNotificationPreferences({
                volume: current.volume,
                tone: toneFromAgent,
                enabled: enabledFromAgent,
            });
        }
    }, [
        agent?.notification_settings?.personal_inbox?.tone,
        agent?.notification_settings?.personal_inbox?.enabled,
    ]);

    /* =========================
       🔌 Inicializar socket
    ========================= */

    useEffect(() => {
        if (!email || !path) return;

        let newSocket;

        const initSocket = async () => {
            const auth = await buildAuthPayload(email);

            newSocket = io(path, {
                transports: ["websocket"],
                autoConnect: true,
                timeout: 10000,
                reconnection: true,
                reconnectionAttempts: Infinity,
                reconnectionDelay: 1000,
                reconnectionDelayMax: 10000,
                auth,
            });

            setSocket(newSocket);
            newSocket.connect();
        };

        initSocket();

        return () => newSocket?.disconnect();

    }, [email, path]);

    /* =========================
       🔌 Conexión
    ========================= */

    useEffect(() => {
        if (!socket) return;

        const onConnect = () => {
            console.log("🟢 [legacy] Socket conectado:", socket.id);
            setOnline(true);
        };

        const onDisconnect = () => {
            console.log("🔴 [legacy] Socket desconectado");
            setOnline(false);
            joinedRef.current = false;
        };

        const onReconnect = (attempt) => {
            console.log("🔄 [legacy] Socket reconnected:", attempt);
            joinedRef.current = false;
        };

        socket.on("connect", onConnect);
        socket.on("disconnect", onDisconnect);
        socket.on("reconnect", onReconnect);

        return () => {
            socket.off("connect", onConnect);
            socket.off("disconnect", onDisconnect);
            socket.off("reconnect", onReconnect);
        };
    }, [socket]);

    useEffect(() => {
        return () => {
            socket?.disconnect();
        };
    }, [socket]);

    /* =========================
       🚪 Join rooms
    ========================= */

    useEffect(() => {
        if (!socket || !online || !phone || !id_customer || joinedRef.current) return;

        console.log("📡 [legacy] Emitiendo join_room...", phone, id_customer);

        socket.emit("join_room", String(phone), (response) => {
            console.log("✅ [legacy] Server confirmó join phone:", response);
        });
        socket.emit("join_room", String(id_customer), (response) => {
            console.log("✅ [legacy] Server confirmó join id_customer:", response);
        });

        joinedRef.current = true;
    }, [online, phone, id_customer]);

    /* =========================
       📩 Mensajes nuevos
    ========================= */

    const isCurrentContactSelected = (message) => {
        if (!refContactDetails.current?._id) return false;
        return (
            String(refContactDetails.current._id) ===
            String(message.id_contact)
        );
    };

    const getNotificationKey = (message) => {
        const stableId = message?.id || message?._id;
        if (stableId) return String(stableId);
        return [
            message?.id_contact || "",
            message?.body || "",
            message?.date_create || "",
            String(message?.incoming),
        ].join("|");
    };

    useEffect(() => {
        if (!socket) return;

        const onNewMessage = (message) => {
            console.log("📨 [legacy] new_message recibido", message);

            if (message?.sync_template !== undefined) {
                dispatch(showTemplateSnackbar(message.sync_template));
            }

            if (message.incoming !== true && message.incoming !== false) return;

            if (isMessageDuplicate(message)) return;

            const isActiveContact = isCurrentContactSelected(message);

            if (
                message.incoming === true &&
                !isDuplicate(message) &&
                !isActiveContact
            ) {
                const notificationKey = getNotificationKey(message);
                const now = Date.now();
                const isDuplicatedNotification =
                    lastNotificationRef.current.key === notificationKey &&
                    now - lastNotificationRef.current.at < 2000;

                if (isDuplicatedNotification) return;

                lastNotificationRef.current = {key: notificationKey, at: now};

                const localPrefs = getNotificationPreferences();

                const shouldNotify = shouldShowNotification(
                    refAgentNotificationSettings.current,
                    {
                        agentId: refIdMyAgent.current,
                        contactAgentId: message?.contact?.id_agent,
                    }
                );

                if (shouldNotify) {
                    // El sonido usa el tono del inbox del mensaje; null = silencio
                    const toneId = resolveInboxTone(
                        refAgentNotificationSettings.current,
                        {
                            agentId: refIdMyAgent.current,
                            contactAgentId: message?.contact?.id_agent,
                            fallbackTone: localPrefs.tone,
                        }
                    );
                    if (toneId) {
                        playNotificationSound(toneId, localPrefs.volume, {
                            interrupt: false,
                            minIntervalMs: 1000,
                        }).catch(() => {});
                    }

                    dispatch(
                        showSnackbar({
                            agentName: message?.agent_inbox?.display_name || "BOT",
                            contactName: message.contact?.first_name || "Contacto",
                            contactPhone: message.contact?.phone,
                        })
                    );

                    showIncomingDesktopNotification({
                        contactName: message.contact?.first_name || "Contacto",
                        contactPhone: message.contact?.phone,
                        contactId: message.contact?._id || message.id_contact,
                        messageId: message._id || message.id,
                        body: message.body,
                    });
                }
            }

            const contact = message.contact;
            const normalizedContact = contact && contact.id_agent === undefined
                ? { ...contact, id_agent: null }
                : contact;

            // id_tray (destino) y previous_tray (origen) viajan al nivel del mensaje,
            // no en message.contact. El emisor legacy manda el mensaje desnudo o
            // envuelto `{ message, agent }` — tolerar ambos shapes. `!= null` para
            // no descartar un id de bandeja 0.
            const prevTray = message.previous_tray ?? message.message?.previous_tray ?? null;
            const syncContact = { ...normalizedContact };
            if (message.id_tray !== undefined) syncContact.id_tray = message.id_tray;
            if (prevTray != null) syncContact.previous_tray = prevTray;
            dispatch(trayQueueSyncContact(syncContact));

            if (isActiveContact) {
                // Tomado desde una bandeja: al confirmarse la asignación por socket,
                // salir de la bandeja y abrir mi inbox.
                if (refActiveTrayId.current && normalizedContact?.id_agent === refIdMyAgent.current) {
                    dispatch(setActiveTray(null));
                    dispatch(selectIdAgent(refIdMyAgent.current));
                }
                // syncContact lleva el id_tray autoritativo del sobre → contactDetails no queda stale.
                handleMessageActions(syncContact, message);
                handleAgentMove(normalizedContact, refAssignedAgentContacts.current);
                dispatch(updateContact(normalizedContact));
            } else {
                updateContactReceived(normalizedContact);
            }
        };

        socket.on("new_message", onNewMessage);
        return () => socket.off("new_message", onNewMessage);

    }, [socket, agent?.notification_settings?.personal_inbox?.tone, agent?.notification_settings?.personal_inbox?.enabled]);

    /* =========================
       🔄 Contact update
    ========================= */

    useEffect(() => {
        if (!socket) return;

        const onUpdateContact = (contact) => {
            const data = contact?.payload || contact;
            const normalized = data.id_agent !== undefined ? data : { ...data, id_agent: null };
            console.log("📬 [legacy] update_contact recibido", { _id: normalized._id, id_agent: normalized.id_agent });
            dispatch(trayQueueSyncContact(normalized));
            updateContactReceived(normalized, refAssignedAgentContacts.current);
        };

        socket.on("update_contact", onUpdateContact);
        return () => socket.off("update_contact", onUpdateContact);
    }, [socket]);

    /* =========================
       🟢 Presencia
    ========================= */

    useEffect(() => {
        if (!socket) return;

        const onPresenceChanged = (data) => {
            const presence = data?.payload || data;
            if (!presence?.agent_email) return;
            dispatch(updateAgentPresence({
                agent_email: presence.agent_email,
                online: presence.online,
                last_seen: presence.last_seen,
            }));
        };

        socket.on("agent_presence_changed", onPresenceChanged);
        return () => socket.off("agent_presence_changed", onPresenceChanged);
    }, [socket]);

    /* =========================
       ❌ Errores
    ========================= */

    useEffect(() => {
        if (!socket) return;

        const onConnectError = (error) => {
            console.error("❌ [legacy] Socket connect_error:", error);
        };

        const onError = (err) => {
            console.error("❌ [legacy] Socket error:", err);
        };

        socket.on("connect_error", onConnectError);
        socket.on("error", onError);

        return () => {
            socket.off("connect_error", onConnectError);
            socket.off("error", onError);
        };
    }, [socket]);

    return {socket, online};
};